import React, { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Modal from "../../../../../components/shared/Modal";
import { useTheme } from '../../../../../contexts/ThemeContext';

import logger from '../../../../../utils/logger';

const FEATURES = [
  {
    title: "Real-Time Expense Tracking",
    description: "Transactions sync automatically; no more manual entries.",
    icon: "📊",
  },
  {
    title: "Customized Alerts",
    description:
      "Get alerts for overspending, upcoming bills, and unusual transactions.",
    icon: "🔔",
  },
  {
    title: "Spending Limits & Budgets",
    description:
      "Set and track category-specific spending limits in real-time.",
    icon: "💰",
  },
  {
    title: "Debt & Credit Monitoring",
    description:
      "Track balances, credit score improvements, and get payoff reminders.",
    icon: "📈",
  },
  {
    title: "Tax Deductions",
    description:
      "Auto-categorize expenses to identify potential tax write-offs.",
    icon: "📑",
  },
  {
    title: "Tokenized Rewards",
    description:
      "Earn points for meeting savings goals and paying bills on time.",
    icon: "🎯",
  },
];

const BankingIntegrationModal = ({ isOpen, onClose, onProceed }) => {
  const [step, setStep] = useState("welcome"); // welcome | interest | connect
  const [interestType, setInterestType] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [email, setEmail] = useState("");
  const { isDarkMode } = useTheme();

  const handleProceed = () => {
    setStep("interest");
  };

  const handleInterestSelection = (type) => {
    setInterestType(type);
    if (type === "now") {
      onProceed();
    }
  };

  const handleSubmit = () => {
    if (interestType === "future" && followUpDate) {
      // Handle follow-up date submission
      logger.info("Follow up on:", followUpDate);
      onClose();
    } else if (interestType === "register" && email) {
      // Handle email registration
      logger.info("Register email:", email);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Unlock Advanced Budgeting & Monitoring"
      size="lg"
    >
      <div className="p-6">
        {step === "welcome" && (
          <>
            <p className="text-gray-600 dark:text-gray-400 mb-8 transition-colors duration-200">
              Connect your bank account to automate your budgeting and unlock
              powerful financial insights.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {FEATURES.map((feature, index) => (
                <div
                  key={index}
                  className={`flex gap-4 p-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02]
                    ${isDarkMode 
                      ? 'bg-gray-800/50 hover:bg-gray-800 border border-gray-700' 
                      : 'bg-gray-50 hover:bg-gray-100 border border-gray-100'}`}
                >
                  <div className="text-2xl filter dark:brightness-90">{feature.icon}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 transition-colors duration-200">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleProceed}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 
                transition-all duration-200 transform hover:scale-[1.02]
                shadow-sm hover:shadow-md dark:shadow-none
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Proceed with Bank Connection
            </button>
          </>
        )}

        {step === "interest" && (
          <div className="space-y-6">
            <div className="space-y-4">
              <button
                onClick={() => handleInterestSelection("now")}
                className={`w-full p-4 text-left rounded-lg transition-all duration-200 transform hover:scale-[1.02]
                  ${isDarkMode 
                    ? 'border-gray-700 hover:bg-gray-800 bg-gray-800/50' 
                    : 'border-gray-200 hover:bg-gray-50 bg-white'}
                  border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">
                  Connect Now
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200">
                  Start syncing your transactions right away
                </p>
              </button>

              <button
                onClick={() => handleInterestSelection("future")}
                className={`w-full p-4 text-left rounded-lg transition-all duration-200 transform hover:scale-[1.02]
                  ${isDarkMode 
                    ? 'border-gray-700 hover:bg-gray-800 bg-gray-800/50' 
                    : 'border-gray-200 hover:bg-gray-50 bg-white'}
                  border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">
                  Remind Me Later
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200">
                  Set a date for us to remind you
                </p>
              </button>

              <button
                onClick={() => handleInterestSelection("register")}
                className={`w-full p-4 text-left rounded-lg transition-all duration-200 transform hover:scale-[1.02]
                  ${isDarkMode 
                    ? 'border-gray-700 hover:bg-gray-800 bg-gray-800/50' 
                    : 'border-gray-200 hover:bg-gray-50 bg-white'}
                  border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">
                  Email Registration
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200">
                  Register your email first
                </p>
              </button>
            </div>

            {interestType === "future" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-200">
                  When should we remind you?
                </label>
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className={`w-full rounded-lg p-2 transition-colors duration-200
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}
                    border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            )}

            {interestType === "register" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-200">
                  Your email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full rounded-lg p-2 transition-colors duration-200
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}
                    border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                  placeholder="Enter your email"
                />
              </div>
            )}

            {(interestType === "future" || interestType === "register") && (
              <button
                onClick={handleSubmit}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 
                  transition-all duration-200 transform hover:scale-[1.02]
                  shadow-sm hover:shadow-md dark:shadow-none
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Submit
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BankingIntegrationModal;
